'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};
		this.cache = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initAsideSizeListener();
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initScrollState();
		this.initScrollToElement();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize aside size listener
	 *
	 * This method automatically resizes the content area and adjusts aside position.
	 */
	initAsideSizeListener() {
		const that = this;
		let asideResizeTimeout;
		this.cache.aside = document.getElementById('aside');
		this.cache.content = document.getElementById('content');
		if (this.cache.aside && this.cache.content) {
			this.cache.h1 = this.cache.content ? this.cache.content.querySelector('h1') : null;
			this.cache.breadcrumbs = document.getElementById('breadcrumbs');
			this.cache.sharingButtons = document.getElementById('sharing-buttons');
			window.addEventListener('resize', () => {
				window.clearTimeout(asideResizeTimeout);
				asideResizeTimeout = window.setTimeout(function() {
					that.checkAsidePosition();
					that.checkAsideSize();
				}, 125);
			});
			that.checkAsidePosition();
			that.checkAsideSize();
			window.setTimeout(function() {
				that.checkAsidePosition();
				that.checkAsideSize();
			}, 125);
		}
	}

	/**
	 * Check aside position and adds or subtracts margin if necessary
	 */
	checkAsidePosition() {
		let adjustTop = 0;
		if (this.cache.h1) {
			adjustTop += this.cache.h1.offsetHeight;
		}
		if (this.cache.breadcrumbs) {
			adjustTop += this.cache.breadcrumbs.offsetHeight;
		}
		if (this.cache.sharingButtons) {
			adjustTop += this.cache.sharingButtons.offsetHeight;
		}
		this.cache.aside.setAttribute(
			'style',
			'margin-top: ' + (parseInt(adjustTop) + 28) + 'px',
		);
	}

	/**
	 * Check aside size and resize content if necessary
	 */
	checkAsideSize() {
		let minHeight = parseInt(this.cache.aside.offsetHeight) + 48;
		if (this.cache.aside.style.marginTop) {
			minHeight += parseInt(this.cache.aside.style.marginTop);
		}
		this.cache.content.setAttribute(
			'style',
			'min-height: ' + parseInt(minHeight) + 'px'
		);
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Initialize scroll state tracking
	 *
	 * This method keeps track of scroll state and applies class to <body> if document has been
	 * scrolled over specific threshold.
	 *
	 * @see {@link https://css-tricks.com/styling-based-on-scroll-position/}
	 */
	initScrollState() {
		if (
			"IntersectionObserver" in window &&
			"IntersectionObserverEntry" in window &&
			"intersectionRatio" in window.IntersectionObserverEntry.prototype
		) {
			const observer = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						document.querySelectorAll('.scrolled').forEach(element => {
							element.classList.remove('scrolled');
						});
					} else {
						document.querySelectorAll('.js-scrolled').forEach(element => {
							element.classList.add('scrolled');
						});
					}
				});
			});
			observer.observe(document.querySelector("#js-scroll-threshold"));
		} else {
			/* Be nice to IE */
			document.body.classList.add('no-js-scroll');
			document.querySelectorAll('.js-scrolled').forEach(element => {
				element.classList.add('scrolled');
			});
		}
	}

	/**
	 * Initialize scroll to element behaviour
	 */
	initScrollToElement() {
		document.querySelectorAll('.js-scroll-to-element').forEach(item => {
			item.addEventListener('click', event => {
				event.preventDefault();
				const id = item.getAttribute('href').substring(1);
				const target = document.getElementById(id);
				let headerHeight = 90;
				if (document.body.classList.contains('adminbar-loaded')) {
					headerHeight += document.getElementById('adminbar').offsetHeight;
				}
				if ('scrollBehavior' in document.documentElement.style) {
					window.scrollTo({
						top: target.getBoundingClientRect().top + window.pageYOffset - headerHeight,
						behavior: 'smooth'
					});
				} else {
					// Be nice to IE
					window.scrollTo(0, target.getBoundingClientRect().top + window.pageYOffset - headerHeight);
				}
			});
		});
	}
}

window._site = new Site();
